import { useEffect, useState } from "react";
import logo from "@/assets/logo.webp";

interface BrandedLoaderProps {
  /** Delay in ms before showing the loader, prevents flash on fast transitions. */
  delay?: number;
  fullscreen?: boolean;
}

/**
 * Branded route-transition loader. Mounted as the Suspense fallback so it only
 * paints when chunk/data resolution exceeds the delay threshold — fast
 * prefetched navigations render the next page instantly with no flash.
 */
const BrandedLoader = ({ delay = 200, fullscreen = true }: BrandedLoaderProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);

  if (!visible) {
    // Reserve layout space without painting anything — avoids CLS while keeping
    // the screen clean during sub-perceptual transitions.
    return <div aria-hidden className={fullscreen ? "min-h-screen" : ""} />;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading page"
      className={`flex flex-col items-center justify-center gap-5 bg-background ${
        fullscreen ? "min-h-screen" : "py-20"
      }`}
    >
      <div className="relative">
        <img
          src={logo}
          alt=""
          aria-hidden
          width={120}
          height={42}
          className="h-10 w-auto invert opacity-90 animate-pulse"
        />
      </div>
      <div className="h-0.5 w-40 overflow-hidden rounded-full bg-muted">
        <div className="loader-bar h-full w-1/3 rounded-full bg-secondary" />
      </div>
      <span className="sr-only">Loading…</span>
      <style>{`
        @keyframes loader-slide {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(420%); }
        }
        .loader-bar { animation: loader-slide 1.1s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default BrandedLoader;
